import React, { useState, useEffect } from 'react';
import { X, FolderOpen, Calendar, User, Tag, Globe, Lock, BarChart3 } from 'lucide-react';
import { superAdminAPI } from '../../services/api';
import { CardLoadingSpinner } from '../ui/LoadingSpinner';
import { showErrorToast } from '../../utils/errorHandling';

const ViewKitModal = ({ isOpen, kit, onClose }) => {
  const [kitDetails, setKitDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [questions, setQuestions] = useState([]);

  useEffect(() => {
    if (isOpen && kit) {
      loadKitDetails();
    }
  }, [isOpen, kit]);

  const loadKitDetails = async () => {
    try {
      setLoading(true);
      // Load detailed kit information
      const [kitResponse, questionsResponse] = await Promise.all([
        superAdminAPI.getKitById(kit.id),
        superAdminAPI.getKitQuestions(kit.id)
      ]);
      
      setKitDetails(kitResponse.data);
      setQuestions(questionsResponse.data || []);
    } catch (error) {
      console.error('Error loading kit details:', error);
      showErrorToast(error, 'Failed to load KIT details');
      // Use the provided kit data as fallback
      setKitDetails(kit);
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'Published': 'bg-green-100 text-green-800',
      'Draft': 'bg-yellow-100 text-yellow-800',
      'Archived': 'bg-gray-100 text-gray-800'
    };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        statusColors[status] || 'bg-gray-100 text-gray-800'
      }`}>
        {status}
      </span>
    );
  };

  if (!isOpen || !kit) return null;

  const displayKit = kitDetails || kit;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <FolderOpen className="h-6 w-6 text-blue-600 mr-3" />
            <h2 className="text-xl font-semibold text-gray-900">KIT Details</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {loading ? (
          <div className="p-8">
            <CardLoadingSpinner />
          </div>
        ) : (
          <div className="p-6 space-y-6">
            {/* Basic Information */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">{displayKit.name}</h3>
                  <p className="text-gray-600">{displayKit.description}</p>
                </div>
                <div className="flex items-center space-x-2">
                  {getStatusBadge(displayKit.status)}
                  {displayKit.isPublic ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      <Globe className="h-3 w-3 mr-1" />
                      Public
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      <Lock className="h-3 w-3 mr-1" />
                      Private
                    </span>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex items-center text-sm text-gray-600">
                  <User className="h-4 w-4 mr-2" />
                  <span>Created by: {displayKit.createdBy || 'Unknown'}</span>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <Calendar className="h-4 w-4 mr-2" />
                  <span>Created: {formatDate(displayKit.createdAt)}</span>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <BarChart3 className="h-4 w-4 mr-2" />
                  <span>Questions: {displayKit.questionCount || questions.length || 0}</span>
                </div>
              </div>
            </div>

            {/* Metadata */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3">Subject & Difficulty</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Subject:</span>
                    <span className="text-sm font-medium text-gray-900">{displayKit.subject || 'Not specified'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Difficulty:</span>
                    <span className="text-sm font-medium text-gray-900">{displayKit.difficulty || 'Not specified'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Tenant Access:</span>
                    <span className="text-sm font-medium text-gray-900">{displayKit.tenantCount || 0} tenants</span>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3">Tags</h4>
                <div className="flex flex-wrap gap-2">
                  {displayKit.tags && displayKit.tags.length > 0 ? (
                    displayKit.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                      >
                        <Tag className="h-3 w-3 mr-1" />
                        {tag}
                      </span>
                    ))
                  ) : (
                    <span className="text-sm text-gray-500">No tags assigned</span>
                  )}
                </div>
              </div>
            </div>

            {/* Questions Preview */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-3">Questions ({questions.length})</h4>
              {questions.length > 0 ? (
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="space-y-3">
                    {questions.slice(0, 5).map((question, index) => (
                      <div key={question.id || index} className="flex items-start justify-between p-3 bg-white rounded border">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {question.questionText || `Question ${index + 1}`}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            {question.subject} • {question.difficulty} • {question.status}
                          </p>
                        </div>
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          question.status === 'Published' ? 'bg-green-100 text-green-800' :
                          question.status === 'Draft' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {question.status}
                        </span>
                      </div>
                    ))}
                    {questions.length > 5 && (
                      <p className="text-sm text-gray-500 text-center py-2">
                        ... and {questions.length - 5} more questions
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="bg-gray-50 rounded-lg p-8 text-center">
                  <FolderOpen className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-sm text-gray-500">No questions in this KIT yet</p>
                </div>
              )}
            </div>

            {/* Statistics */}
            {displayKit.stats && (
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3">Statistics</h4>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-blue-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-blue-600">{displayKit.stats.totalQuestions || 0}</p>
                    <p className="text-xs text-blue-600">Total Questions</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-green-600">{displayKit.stats.publishedQuestions || 0}</p>
                    <p className="text-xs text-green-600">Published</p>
                  </div>
                  <div className="bg-yellow-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-yellow-600">{displayKit.stats.draftQuestions || 0}</p>
                    <p className="text-xs text-yellow-600">Draft</p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-purple-600">{displayKit.stats.usageCount || 0}</p>
                    <p className="text-xs text-purple-600">Times Used</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-end p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewKitModal;
